import type { AIProvider } from '../../config/ai-models'

import type { RecommendationLoggingConfig } from './logging.types'

// Shared types for GIF data structure
// This is the canonical source of truth for all GIF-related types
//
// The frame-based display-analysis taxonomy (ContentType / CoreEmotion /
// EmotionalIntensity / ReactionType / SocialDynamics + GifVisualElements /
// GifEmotionalProfile / useCases) was retired in the 2026-07-10 unified
// ingestion pipeline. Search + display now read the denormalized retrieval
// prose (`retrievalSearch`). Only `ExplicitRating` (a hard filter) survives
// from the old enums.

// Enums for standardized values
export enum ExplicitRating {
  G = 'G',
  PG = 'PG',
  PG_13 = 'PG-13',
  R = 'R',
}

// Nested interfaces for complex objects
export interface GifReference {
  /** Name of show/movie/meme/viral video, or null if unknown */
  source: string | null
  /** Character names, meme titles, or quotable phrases people would recognize */
  keywords: string[]
  /** Year of the show/movie/meme/viral video, or null if unknown */
  year: number | null
}

/**
 * Denormalized retrieval prose copied onto the gifs doc at approval (see
 * RetrievalReviewService.approve + the unified-pipeline migration). This is
 * what search + display read now that the frame-based display taxonomy is gone.
 */
export interface RetrievalSearch {
  /** 2–4 sentence "what happens" — the display title / OG description source. */
  narrative: string
  /** Concrete second-person uses ("Use when your friend…"). */
  scenarioExamples: string[]
  /** Emotions/situations this works for — the display + search chips. */
  breadthTags: string[]
  /** What sets this GIF apart from others in its bucket. */
  distinctiveFeatures: string[]
}

export interface GifPopularity {
  /** Number of times this gif has been copied/saved */
  copies: number
  /** Number of times this gif has been favorited */
  favorited: number
}

// Core gif data structure (the hand-curated / operational fields that get
// stored permanently). The display-analysis fields are gone; the AI-facing
// prose lives on the retrieval record and is denormalized to `retrievalSearch`
// at approval.
export interface GifCoreData {
  /** Hand-curated across review passes; never regenerated. */
  reference: GifReference
  /** Hard content-rating filter. */
  explicitRating: ExplicitRating
}

// Complete gif data structure for creating new gifs
export interface CreateGifData extends GifCoreData {
  // Basic metadata
  durationSeconds: number
  gifUrl: string

  // Computed fields
  popularity: GifPopularity
}

/**
 * Exact-identity signals persisted on a `gifs` doc at ingest, used by the
 * duplicate-detection warning on /admin/curation. All optional — pre-feature
 * docs, file uploads, and partial backfills lack some or all of them. These are
 * small scalars, safe to ship to every client over the public gifs subscription.
 */
export interface GifIdentitySignals {
  /**
   * SHA-256 (hex) of the ORIGINAL pre-transcode input bytes. Present only for
   * gifs ingested after this feature shipped — NOT backfillable, since the
   * original bytes of existing gifs are gone (only the re-encoded output was
   * stored). The strongest signal: identical uploads share this exactly.
   */
  inputSha256?: string
  /**
   * SHA-256 (hex) of the re-encoded output GIF bytes. Backfillable from the
   * stored binary, but depends on ffmpeg re-encode determinism, so treat it as
   * a bonus signal rather than a guarantee.
   */
  normalizedGifSha256?: string
  /**
   * Stable per-platform identity parsed from the source URL at ingest, e.g.
   * `tenor:12345`, `reddit:post:abc`, `reddit:comment:xyz`, `giphy:<id>`.
   * Undefined for file uploads and unsupported/unparseable URLs.
   */
  platformId?: string
  /**
   * Source URL reduced to a canonical form (lowercase host; protocol, query,
   * hash, and trailing slash stripped) for exact-URL matching. Undefined for
   * file uploads (`file-upload`) and empty source urls.
   */
  normalizedSourceUrl?: string
}

/**
 * Read shape of a `gifs` Firestore doc as the backend consumes it (gif-preview
 * OG tags, template + analytics). Tolerates BOTH data shapes during the
 * unified-pipeline migration: post-migration docs carry `retrievalSearch` +
 * `published`/`publishedAt`; pre-migration docs may still have the legacy
 * display fields, so those are typed as optional/loose here for graceful
 * fallback reads. Never construct one of these — it is a read-only view.
 */
export interface GifDocumentRead extends GifIdentitySignals {
  reference?: GifReference
  explicitRating?: ExplicitRating
  /** Post-migration search/display prose (present once approved). */
  retrievalSearch?: RetrievalSearch
  /** Set on first approval; presence = published (searchable/recommendable). */
  publishedAt?: string
  published?: boolean
  /** Legacy pre-migration display fields — read defensively, never written. */
  visualElements?: { description?: string; visualTags?: string[]; text?: string }
  emotionalProfile?: { coreEmotion?: string | null; emotionalTags?: string[] }
  useCases?: string[]
}

// Request/Response types for GIF media conversion and storage
export interface ConvertAndStoreMediaRequest {
  mediaFile: {
    data: string // base64 encoded file data
    mediaType: string // 'image/gif', 'video/mp4', 'image/webp', 'video/webm', 'video/mov'
  }
  options?: {
    maxWidth?: number // maximum width for output GIF (default: 720)
    maxHeight?: number // maximum height for output GIF (default: 720)
    frameRate?: number // target frame rate (default: 24)
    quality?: number // output quality 1-100 (default: 95)
  }
}

export interface ConvertAndStoreMediaResponse {
  success: boolean
  data?: {
    gifUrl: string // public URL of the stored GIF
    fileName: string // UUID filename of the stored GIF
    originalFormat: string // original format of the input file
    durationSeconds: number // duration of the GIF
    fileSizeBytes: number // size of the converted GIF file
  }
  error?: string
}

// Request/Response types for GIF recommendations
export interface RecommendGifsRequest {
  // Context for the recommendation
  context?: {
    conversationSnippet?: string // Optional snippet of conversation context
    userIntent?: string // What the user wants to convey
  }

  // User preferences and filters
  preferences?: {
    maxResults?: number // Maximum number of recommendations (default: 8)
    explicitRating?: ExplicitRating[] // Allowed content ratings (default: [G, PG])
    maxDuration?: number // Maximum GIF duration in seconds
    minDuration?: number // Minimum GIF duration in seconds
  }

  // GIF exclusion for avoiding duplicates in refinement sessions
  excludeGifIds?: string[] // GIFs to exclude from search results (client-tracked shown GIFs)

  // Refinement options for iterative refinement functionality
  refinement?: {
    similarToGifId?: string // "Pretty close" - GIF ID to find similar recommendations
    excludeSimilarToGifIds?: string[] // "None of these" - GIF IDs to avoid similarity to
    feedback?: 'similar' | 'different' // Refinement type (replaces 'like'/'dislike')
    userHint?: string // Optional one-sentence informal guidance for different gif selection
  }

  // AI model to use for recommendation analysis
  model?: AIProvider

  /**
   * Recommendation preset name (model + limits + temperature). Validated
   * server-side against RECOMMENDATION_PRESETS; unknown values fall back to
   * the default. Plain string so this shared type file stays free of module
   * imports.
   */
  preset?: string

  // Optional logging configuration
  logging?: RecommendationLoggingConfig
}

export interface RecommendationCandidate {
  gifId: string
  rank: number // 1-based ranking position
  reasoning: string // Why this GIF was recommended
  strengths: string[] // What makes this GIF a good choice
  limitations?: string[] // Optional - what prevents this from ranking higher
  stanceLabel?: string // Which response stance this pick belongs to (grouped output)
  /**
   * Optional short line of the sender's own words, sent alongside the GIF (the
   * message text typed above it). The reranker attaches one only when a pick
   * needs it — to supply a missing referent, reassign the GIF's embedded
   * speaker, or bridge a leap. Absent on picks that stand on their own, and on
   * the similar-to / stance-expansion paths (which never caption).
   */
  caption?: string
}

/**
 * A named response "move" the reranker mapped for this conversation. Gifs
 * within a stance are alternates of the same move; across stances the moves
 * are distinct. Absent on the similar-to path (ungrouped flat list).
 */
export interface RecommendationStanceView {
  label: string // Short free-form phrase (2–4 words) naming the sender's move
  rationale: string // One short sentence on why this move fits
  gifIds: string[] // Ids grouped under this stance, in rank order
}

// AI ranking response
export interface AIRankingResponse {
  rankedCandidates: {
    gifId: string
    rank: number
    reasoning: string
    strengths: string[]
    limitations?: string[]
  }[]
}

// Ranking guidance interface (matches backend)
export interface RankingGuidance {
  primaryFocus: string
  userVoiceGuidance: string
  mimicryGuidance: string
  emotionalRange: string
  contextualNuances: string
  avoidance: string
  specialConsiderations: string
}

// Token usage tracking interface for admin monitoring
export interface TokenUsageInfo {
  prompt_tokens?: number
  completion_tokens?: number
  input_tokens?: number
  output_tokens?: number
  total_tokens?: number
  estimated_cost_usd: {
    input: string
    output: string
    total: string
  }
  model: string
}

export interface RecommendGifsResponse {
  success: boolean
  data?: {
    recommendations: RecommendationCandidate[]
    /**
     * Stance-grouped view of the picks. Present when the reranker grouped its
     * picks into moves; absent for the similar-to path. The flat
     * `recommendations` list is always populated regardless.
     */
    stances?: RecommendationStanceView[]
    totalCandidatesEvaluated: number
    processingTimeMs: number
    timingBreakdown: {
      documentFetchMs: number // Time to fetch and filter documents
      aiProcessingMs: number // Time for AI selection and ranking
    }
    tokenUsage?: {
      processing?: TokenUsageInfo
    }
  }
  error?: string
}

/**
 * Request for expandRecommendationStance ("more like this"): expand one stance
 * with fresh embedding-neighbors of its current gifs. Zero LLM calls.
 */
export interface ExpandRecommendationStanceRequest {
  /** The stance's current gif ids (seeds). 1–5. */
  gifIds: string[]
  /** Everything already shown across all stances — never re-surface these. */
  excludeGifIds: string[]
  /** How many to return (server-clamped 1–8; default 4). */
  limit?: number
  /** Allowed content ratings, same shape as the other recommendation callables. */
  contentRatings?: ExplicitRating[]
}

export interface ExpandRecommendationStanceResponse {
  success: boolean
  data?: {
    /** Fresh candidates in the same shape the similar-to path returns. */
    recommendations: RecommendationCandidate[]
  }
  error?: string
}

/**
 * Request for retrieveCandidatePool: embedding-based candidate retrieval for
 * eval-fixture curation. Reuses the recommendation request's context/
 * preferences shape; no LLM call is made.
 */
export interface RetrieveCandidatePoolRequest {
  context?: RecommendGifsRequest['context']
  preferences?: RecommendGifsRequest['preferences']
  /** How many candidates to return (server-clamped; default 24). */
  k?: number
}

export interface RetrieveCandidatePoolResponse {
  success: boolean
  data?: {
    /** Gif ids in descending cosine-similarity order. */
    candidates: { gifId: string; score: number }[]
    /** Size of the gifRetrieval collection the pool was drawn from. */
    retrievalRecordCount: number
  }
  error?: string
}

/**
 * One reaction to a concrete gif during the "explore & react" fixture-authoring
 * flow. The owner's taste works by recognition — a fast verdict on a real gif —
 * not by writing an abstract essay. These reactions are the raw signal the LLM
 * compiles into a fixture `note`.
 */
export interface FixtureNoteReaction {
  gifId: string
  /** `good` → a viable move; `trap` → tempting-but-wrong; `skip` → no signal. */
  verdict: 'good' | 'trap' | 'skip'
  /** Optional one-tap reason chip labels, e.g. "wrong tone", "too mean". */
  reasons?: string[]
  /** Optional free-text note the reactor typed. */
  comment?: string
}

/**
 * An optional stance-level reaction: the reactor's verdict on a whole response
 * "move" the recommender proposed, rather than an individual gif.
 */
export interface FixtureNoteStanceReaction {
  label: string
  /** `real-move` → this move is on the table; `off` → the move doesn't fit. */
  verdict: 'real-move' | 'off'
  reasons?: string[]
}

/**
 * One bucketed gif carried into fixture-meta drafting: the gate/diagnostic
 * bucket it lives in plus its id. The drafter fetches each id's retrieval
 * narrative so it can weave WHY a trap is tempting-but-wrong into the note (the
 * judge only ever sees the note, never the id lists).
 */
export interface DraftFixtureBucketGif {
  gifId: string
  /**
   * `trap` → tempting-but-wrong (the note must name why it's wrong); `forbidden`
   * → never acceptable; `good` → a viable move the note should describe.
   */
  bucket: 'trap' | 'forbidden' | 'good'
}

/**
 * Request for draftFixtureMeta: propose the organizational fields of a fixture
 * (slug/description/tags) plus the load-bearing `note` from the conversation and
 * the curator's bucketed gifs — so authoring is "here's a conversation, this gif
 * was a trap, these are good" rather than hand-writing metadata.
 *
 * At least one context field must be present; validation additionally requires
 * either a bucketed gif, a non-skip reaction, or an `existingNote` to revise.
 * `reactions` is the optional Explore & react signal; `buckets` is the common
 * flow (type conversation, run recommender, click trap/good on results).
 */
export interface DraftFixtureMetaRequest {
  context: {
    conversationSnippet?: string
    userIntent?: string
  }
  /**
   * The trap/forbidden/good gifs the curator has bucketed (from clicking results
   * or from an Explore & react pass). Server clamps to ≤60. Their retrieval
   * narratives are fetched so the note can explain the trap rationale in prose.
   */
  buckets?: DraftFixtureBucketGif[]
  /**
   * Optional gif-level reactions from an Explore & react pass (verdicts + reason
   * chips/comments). Merged with `buckets` as additional signal. Server clamps.
   */
  reactions?: FixtureNoteReaction[]
  /** Optional stance-level reactions on the recommender's proposed moves. */
  stanceReactions?: FixtureNoteStanceReaction[]
  /**
   * When present, REVISE this note with the new signal rather than drafting
   * fresh — preserve what it gets right, integrate the new evidence.
   */
  existingNote?: string
}

/**
 * The drafted fixture metadata: the load-bearing `note` plus the organizational
 * fields the curator otherwise hand-authors. The UI drops each into its form
 * field marked as a draft; a human edit clears the draft flag for that field.
 */
export interface DraftFixtureMeta {
  /** The compiled 2–5 sentence fixture note, ready for the author to edit. */
  note: string
  /** Kebab-case scenario slug, e.g. `friend-admits-you-were-right`. */
  slug: string
  /** One-line description of what the fixture covers. */
  description: string
  /** 3–5 short lowercase tags (situation type, register, e.g. "1on1", "playful"). */
  tags: string[]
}

export interface DraftFixtureMetaResponse {
  success: boolean
  data?: DraftFixtureMeta
  error?: string
}

// Type guards for enum validation
export const isExplicitRating = (value: string): value is ExplicitRating => {
  return Object.values(ExplicitRating).includes(value as ExplicitRating)
}

// Conversation formatting types
export interface ConversationTurn {
  id: string
  speaker: string
  message: string
  originalText?: string
}

export interface ParsedConversation {
  turns: ConversationTurn[]
  identifiedSpeakers: string[]
  confidence: number
  rawText: string
}

export interface ConversationFormatRequest {
  rawText: string
  model?: AIProvider
  userContext?: {
    displayName?: string | null
    email?: string | null
  }
}

export interface ConversationFormatResponse {
  success: boolean
  data?: ParsedConversation
  error?: string
}

// Image-to-conversation conversion types
export interface ImageConversationRequest {
  /** Base64 encoded image data */
  imageData: string
  /** MIME type of the image (e.g., 'image/png', 'image/jpeg') */
  imageType: string
  /** Optional user context for better speaker identification */
  userContext?: {
    displayName?: string | null
    email?: string | null
  }
}

export interface ImageConversationResponse {
  success: boolean
  data?: {
    /** Raw text representation for user editing */
    rawText: string
    /** Processed conversation data with structured turns */
    processedConversation: ParsedConversation
  }
  error?: string
}

/**
 * Preprocessed GIF data: extracted frames used for preview/analysis workflows.
 * Returned by VideoProcessingService.processVideo and threaded through
 * MediaProcessingService so callers (processMediaUnified, extractGifFromUrl)
 * can surface frames for display without a separate round-trip.
 */
export interface PreprocessedGifData {
  frames: { data: string; mediaType: string; timestamp: number }[]
  frameCount: number
  durationSeconds: number
  /** Original media type of the input file (e.g. 'image/gif', 'video/mp4'). */
  originalFormat: string
}

// Two-Phase Processing Types (Queue Workflow)

/** Processing status for GIFs in the queue workflow */
export enum ProcessingStatus {
  PENDING_ANALYSIS = 'pending_analysis',
  ANALYZING = 'analyzing',
  COMPLETED = 'completed',
  ANALYSIS_FAILED = 'analysis_failed',
}

/** Request type for queueGifUpload function - Phase 1 quick upload */
export interface QueueGifUploadRequest {
  input: {
    type: 'url' | 'file'
    data: string // URL or base64 file data
    mediaType?: string // Required for file uploads, optional for URLs
  }
  options?: {
    maxWidth?: number // default: 720
    maxHeight?: number // default: 720
    frameRate?: number // default: 24
    quality?: number // default: 95
  }
}

/** Response type for queueGifUpload function */
export interface QueueGifUploadResponse {
  success: boolean
  data?: {
    gifId: string
    gifUrl: string
    videoUrl: string
    processingStatus: ProcessingStatus
    fileSizeBytes: number
    durationSeconds: number
  }
  error?: string
}

/**
 * Request type for retryGifAnalysis: re-run the unified retrieval analysis on a
 * gif whose create-trigger analysis failed or never fired. Admin-gated.
 */
export interface RetryGifAnalysisRequest {
  gifId: string
}

/** Response type for retryGifAnalysis */
export interface RetryGifAnalysisResponse {
  success: boolean
  data?: {
    gifId: string
    processingStatus: ProcessingStatus
  }
  error?: string
}

/**
 * Request type for deleteGifCompletely: fully remove a gif — the `gifs` doc,
 * its paired `gifRetrieval` record (doc id === gif id), and the Storage GIF +
 * MP4 binaries. Admin-gated; an explicit, confirmed curator action from the
 * Curation review card. There is no undo.
 */
export interface DeleteGifCompletelyRequest {
  gifId: string
}

/** Response type for deleteGifCompletely. */
export interface DeleteGifCompletelyResponse {
  success: boolean
  data?: {
    gifId: string
    /** True when a paired gifRetrieval record existed and was removed. */
    deletedRetrievalRecord: boolean
  }
  error?: string
}
